import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Dimensions } from '@/shared/theme';
import { ResultTitle, ResultBody } from '@/shared/resultText';
import { StartOverModal } from '@/components/StartOverModal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { VIEW_NOT_ELIGIBLE } from '@/consts/events';
import telescopeIcon from '@/assets/images/telescope.svg';

// A POST_REGISTRATION_PATHS route (Layout.tsx): a plain, unframed, normal
// page-scroll screen with no rail/phase-bar to anchor to. min-height +
// justify-content:center keep this vertically centered (Boris `.rg-result`)
// instead of clumping at the top of an otherwise-empty cream page, matching
// EligibilityResultStep's already-fixed terminal-screen pattern.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - ${Dimensions.headerHeight}px);
  gap: ${Spacing.x2}px;
  text-align: center;
  padding: ${Spacing.x4}px ${Spacing.x2}px;
`;

const IllustrationWrapper = styled.div`
  width: 144px;
  height: 144px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IllustrationImage = styled.img`
  width: 144px;
  height: 144px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x1_5}px;
  width: 100%;
  text-align: center;
`;

// Boris result-variant audit (RND-35692 Phase 3): Boris's prototype splits
// "not eligible" into three named screens (reg-app-dt.jsx / reg-screen-frame.html):
// r-ineligible, r-control, r-noqualify. Investigated whether this app needs the
// same 3-way split; concluded one screen is correct, not a gap:
//   - r-ineligible (verbatim CMS control-group legal text, left-aligned doc
//     layout) is already built separately as EligibilityResultStep's
//     `control-group` phase, not here -- its body is the mandatory CMS
//     Participation Agreement text (ineligibility-result-code
//     'not-eligible-control-group').
//   - r-control (a shorter, friendlier control-group message with a "Back to
//     start" CTA) is Boris's own alternate copy for that SAME result code.
//     Once the mandatory verbatim legal text is shown for it, there is no
//     second real taxonomy value left for this variant to represent.
//   - r-noqualify (health-screening "no qualifying condition", warm badge,
//     "Health screening" eyebrow) maps to the pre-CMS path: when condition
//     screening yields zero candidate tracks, useEligibilitySubmission routes
//     straight here without ever calling CMS. This screen ALSO catches the
//     post-CMS rejection codes (not-eligible-not-medicare/-services/
//     -diagnoses/-clinical-exclusion/-mismatch) that EligibilityResultStep
//     funnels here when no track comes back eligible. The copy below is
//     deliberately generic so it reads accurately for both origins without
//     attributing a specific reason; Boris's "Health screening" framing would
//     misattribute the post-CMS-rejection case, so it is not reused here.
const NotEligibleStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackView } = useAnalytics();
  // No registration data needed for this step
  const [showStartOverModal, setShowStartOverModal] = useState(false);

  // Integrate useAppFlow with back navigation blocking
  const { resetBlocker } = useAppFlow(STEP_KEYS.NOT_ELIGIBLE, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
  });

  useEffect(() => {
    trackView({ event: VIEW_NOT_ELIGIBLE });
  }, [trackView]);

  const handleStartOver = () => {
    // Navigate to landing page (which will clear all data on mount)
    setShowStartOverModal(false);
    navigate('/');
  };

  return (
    <Container>
      <IllustrationWrapper>
        <IllustrationImage src={telescopeIcon} alt="" aria-hidden="true" />
      </IllustrationWrapper>

      <ContentWrapper>
        <ResultTitle size="h2" centered>
          {t('not-eligible.title')}
        </ResultTitle>

        <ResultBody size="p1" centered>
          {t('not-eligible.message')}
        </ResultBody>
      </ContentWrapper>

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
    </Container>
  );
};

export { NotEligibleStep };
